// src/pages/ArtistPublicPage.jsx
// Public artist portfolio page — accessible at gigwave.in/artist/:slug
// No authentication required to view.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FollowButton from '../components/FollowButton';
import {
  getArtistProfileBySlug,
  getArtistStats,
  getArtistPublicGigs,
} from '../firebase-utils';

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', icon: '📸', color: '#E1306C', base: 'https://instagram.com/' },
  { key: 'youtube', label: 'YouTube', icon: '▶️', color: '#FF0000', base: 'https://youtube.com/' },
  { key: 'spotify', label: 'Spotify', icon: '🎵', color: '#1DB954', base: 'https://open.spotify.com/artist/' },
  { key: 'facebook', label: 'Facebook', icon: '👍', color: '#1877F2', base: 'https://facebook.com/' },
  { key: 'tiktok', label: 'TikTok', icon: '🎵', color: '#ee1d52', base: 'https://tiktok.com/@' },
  { key: 'twitter', label: 'Twitter', icon: '🐦', color: '#1DA1F2', base: 'https://twitter.com/' },
  { key: 'soundcloud', label: 'SoundCloud', icon: '☁️', color: '#FF5500', base: 'https://soundcloud.com/' },
];

const getSocialUrl = (platform, value) => {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  const p = SOCIAL_PLATFORMS.find(p => p.key === platform);
  if (!p) return value;
  return `${p.base}${value.replace('@', '')}`;
};

export default function ArtistPublicPage({ slug, onBack }) {
  const { currentUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentGigs, setRecentGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }

    const load = async () => {
      setLoading(true);
      try {
        const profileData = await getArtistProfileBySlug(slug);
        if (!profileData) { setNotFound(true); setLoading(false); return; }
        setProfile(profileData);

        // Load stats and gigs in parallel
        const [statsData, gigsData] = await Promise.all([
          getArtistStats(profileData.userId),
          getArtistPublicGigs(profileData.userId, 5),
        ]);
        setStats(statsData);
        setRecentGigs(gigsData);
      } catch (err) {
        console.error('Error loading public profile:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  // Update page title
  useEffect(() => {
    if (profile) {
      document.title = `${profile.artistName} — GigWave`;
    }
    return () => { document.title = 'GigWave'; };
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen rock-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-electric mx-auto mb-4"></div>
          <p className="text-electric font-bold text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen rock-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">🎸</p>
          <h1 className="text-3xl font-bold text-white mb-2">Artist not found</h1>
          <p className="text-gray-400 mb-6">This profile doesn't exist or the link may have changed.</p>
          <button onClick={onBack || (() => window.location.href = '/')}
            className="btn btn-electric">← Back to GigWave</button>
        </div>
      </div>
    );
  }

  const shareUrl = `https://gigwave.in/artist/${profile.slug || slug}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('✅ Profile link copied!');
    }).catch(() => {
      prompt('Copy this link:', shareUrl);
    });
  };

  // Profile disabled — show private message instead
  if (profile?.profileDisabled) {
    return (
      <div className="min-h-screen rock-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">🔒</p>
          <h1 className="text-3xl font-bold text-white mb-2">Profile not available</h1>
          <p className="text-gray-400 mb-6">This artist has made their profile private.</p>
          <button onClick={onBack || (() => window.location.href = '/')}
            className="btn btn-electric">← Back to GigWave</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rock-background">

      {/* Top nav */}
      <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack || (() => window.location.href = '/')}
            className="text-electric font-bold text-sm hover:text-neon transition">
            ← GigWave
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleCopyLink}
              className="text-sm px-3 py-1.5 rounded-lg font-bold transition"
              style={{ background: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>
              🔗 Share Profile
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">

        {/* ── Hero card ── */}
        <div className="gig-card p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6">

            {/* Avatar */}
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl flex-shrink-0 flex items-center justify-center text-4xl font-bold"
              style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.3), rgba(168,85,247,0.3))', border: '2px solid rgba(0,212,255,0.4)' }}>
              {profile.profilePhoto ? (
                <img src={profile.profilePhoto} alt={profile.artistName}
                  className="w-full h-full object-cover rounded-2xl" />
              ) : (
                <span style={{ color: '#00d4ff' }}>{(profile.artistName || '?')[0].toUpperCase()}</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">{profile.artistName}</h1>
              {profile.fullName && profile.fullName !== profile.artistName && (
                <p className="text-gray-400 text-sm mb-2">{profile.fullName}</p>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {profile.genre && (
                  <span className="px-3 py-1 rounded-full text-sm font-bold"
                    style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>
                    🎸 {profile.genre}
                  </span>
                )}
                {profile.location && (
                  <span className="px-3 py-1 rounded-full text-sm"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}>
                    📍 {profile.location}
                  </span>
                )}
                {profile.isAvailable !== false ? (
                  <span className="px-3 py-1 rounded-full text-sm font-bold"
                    style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>
                    ✅ Available for Hire
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-sm font-bold"
                    style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>
                    ❌ Not Available
                  </span>
                )}
              </div>

              {/* GigWave follow */}
              <div className="flex items-center gap-3 flex-wrap">
                <FollowButton
                  artistId={profile.userId}
                  artistName={profile.artistName}
                  size="md"
                  showCount={true}
                  initialCount={profile.gigwaveFollowerCount || 0}
                  onAuthRequired={() => {
                    alert('Sign in to GigWave to follow this artist and get notified about their gigs!');
                    window.location.href = '/';
                  }}
                />
                {profile.gigwaveFollowerCount > 0 && (
                  <span className="text-sm text-gray-500">
                    {profile.gigwaveFollowerCount.toLocaleString()} GigWave {profile.gigwaveFollowerCount === 1 ? 'follower' : 'followers'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats ── */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: '🎸', label: 'Gigs Performed', value: stats.completedGigs || stats.totalGigs || 0, color: '#00d4ff' },
              { icon: '⭐', label: 'Avg Rating', value: stats.averageRating ? `${stats.averageRating}/5` : 'N/A', color: '#f59e0b', sub: stats.ratingCount > 0 ? `${stats.ratingCount} ratings` : null },
              { icon: '👥', label: 'Total Audience', value: (stats.totalAudience || 0).toLocaleString(), color: '#a855f7' },
              { icon: '⚡', label: 'Song Votes', value: (stats.totalVotes || 0).toLocaleString(), color: '#22c55e' },
            ].map(({ icon, label, value, color, sub }) => (
              <div key={label} className="rounded-xl p-4 text-center"
                style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
                <div className="text-2xl mb-1">{icon}</div>
                <div className="text-xl font-bold text-white">{value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{label}</div>
                {sub && <div className="text-xs mt-0.5" style={{ color }}>{sub}</div>}
              </div>
            ))}
          </div>
        )}

        {/* ── Bio ── */}
        {profile.bio && (
          <div className="gig-card p-6">
            <p className="text-electric text-xs font-bold uppercase tracking-wider mb-3">About</p>
            <p className="text-gray-300 leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </div>
        )}

        {/* ── Pricing ── */}
        {(profile.priceMin || profile.priceMax) && (
          <div className="gig-card p-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-electric text-xs font-bold uppercase tracking-wider mb-1">💰 Booking Rate</p>
              <p className="text-2xl font-bold text-neon">
                ₹{profile.priceMin ? Number(profile.priceMin).toLocaleString() : '?'}
                {profile.priceMax ? ` – ₹${Number(profile.priceMax).toLocaleString()}` : '+'}
              </p>
              <p className="text-gray-400 text-sm">per event</p>
            </div>
            {profile.availabilityNote && (
              <div className="text-right">
                <p className="text-yellow-400 text-sm">📅 {profile.availabilityNote}</p>
              </div>
            )}
          </div>
        )}

        {/* ── Social Media ── */}
        {profile.socialMedia && Object.values(profile.socialMedia).some(v => v) && (
          <div className="gig-card p-6">
            <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">🔗 Follow on Social Media</p>
            <div className="flex flex-wrap gap-3">
              {SOCIAL_PLATFORMS.map(({ key, label, icon, color }) => {
                const val = profile.socialMedia[key];
                if (!val) return null;
                const url = getSocialUrl(key, val);
                return (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition hover:opacity-80"
                    style={{ background: `${color}15`, color, border: `1px solid ${color}40` }}>
                    <span>{icon}</span><span>{label}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Social follower counts ── */}
        {profile.followerCounts && !profile.followerCountsBlocked && Object.values(profile.followerCounts).some(v => v) && (
          <div className="gig-card p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-electric text-xs font-bold uppercase tracking-wider">📊 Follower Counts</p>
              {profile.followerCountsUpdatedAt && (
                <span className="text-xs text-gray-500">
                  Updated {new Date(
                    profile.followerCountsUpdatedAt?.seconds
                      ? profile.followerCountsUpdatedAt.seconds * 1000
                      : profile.followerCountsUpdatedAt
                  ).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SOCIAL_PLATFORMS.map(({ key, icon, label, color }) => {
                const val = profile.followerCounts[key];
                if (!val) return null;
                return (
                  <div key={key} className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
                    <span className="text-2xl">{icon}</span>
                    <div>
                      <p className="text-white font-bold text-lg leading-tight">
                        {Number(val).toLocaleString()}
                      </p>
                      <p className="text-xs" style={{ color }}>{label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Recent Gigs ── */}
        {recentGigs.length > 0 && (
          <div className="gig-card p-6">
            <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">🎸 Recent Performances</p>
            <div className="space-y-3">
              {recentGigs.map(gig => (
                <div key={gig.id} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div>
                    <p className="text-white font-semibold">{gig.venueName}</p>
                    <p className="text-gray-400 text-xs">
                      📅 {gig.gigDate}{gig.gigTime ? ` at ${gig.gigTime}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-3 text-right flex-shrink-0">
                    {gig.audience > 0 && (
                      <div>
                        <p className="text-white font-bold text-sm">{gig.audience}</p>
                        <p className="text-gray-500 text-xs">audience</p>
                      </div>
                    )}
                    {gig.avgRating && (
                      <div>
                        <p className="text-yellow-400 font-bold text-sm">⭐ {gig.avgRating}</p>
                        <p className="text-gray-500 text-xs">rating</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Hire CTA ── */}
        {profile.isAvailable !== false && (
          <div className="gig-card p-6 text-center"
            style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.25)' }}>
            <p className="text-white font-bold text-lg mb-1">Book {profile.artistName} for your venue</p>
            <p className="text-gray-400 text-sm mb-4">
              Create a venue account on GigWave to send a hire request directly.
            </p>
            <a href="/"
              className="inline-block btn btn-electric">
              🏢 Open GigWave to Hire →
            </a>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="text-center py-4">
          <a href="/" className="text-gray-500 text-sm hover:text-electric transition">
            🎸 Powered by GigWave
          </a>
        </div>
      </div>
    </div>
  );
}
